'use client';
import { useBrowserEffect } from '@mnd-frontend/hooks';
import type React from 'react';
import { forwardRef, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

const SvgWrapper = styled.div<{
  $minWidth: number;
  $minHeight: number;
}>`
  flex-basis: 0;
  flex-grow: 1;
  min-width: ${props => props.$minWidth}px;
  min-height: ${props => props.$minHeight}px;
`;

const SvgElement = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;

  rect {
    outline: none;
  }
`;

type Props = {
  children: React.ReactNode;
  minWidth: number;
  height?: number;
  onClick?: (id: any, event: React.MouseEvent<SVGSVGElement>) => void;
  onChangeWidth?: (width: number) => void;
  onMouseMove?: (x?: number, y?: number) => void;
};

const SvgResponsive = forwardRef<SVGSVGElement, Props>(
  ({ children, minWidth, height, onChangeWidth, onMouseMove, onClick }, ref) => {
    const [width, setWidth] = useState<number | undefined>(undefined);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useBrowserEffect(() => {
      if (!wrapperRef.current) return;

      const handleResize = () => {
        const newWidth = Math.max(minWidth, wrapperRef?.current?.offsetWidth || 0);
        setWidth(newWidth);
        onChangeWidth?.(newWidth);
      };
      const resizeObserver = new window.ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(wrapperRef.current);
      handleResize();
      return () => resizeObserver.disconnect();
    }, [onChangeWidth, minWidth]);

    const updateMouse = useCallback((x?: number, y?: number) => onMouseMove?.(x, y), [onMouseMove]);

    return (
      <SvgWrapper ref={wrapperRef} $minWidth={minWidth} $minHeight={height || 0}>
        {width && height && (
          <SvgElement
            ref={ref}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMinYMid meet"
            width={width}
            height={height}
            vectorEffect="non-scaling-stroke"
            onMouseMove={event => {
              const matrix = event?.currentTarget?.getScreenCTM();
              if (!matrix) return;
              const { a, d, e, f } = matrix;
              updateMouse((event.clientX - e) / a, (event.clientY - f) / d);
            }}
            onMouseLeave={() => updateMouse(undefined, undefined)}
            onClick={event => onClick?.(undefined, event)}
          >
            {children}
          </SvgElement>
        )}
      </SvgWrapper>
    );
  },
);

export default SvgResponsive;
